import { getAllDocs as getDocArticles } from '@naumu/docs';
import type { DocArticle } from '@naumu/docs';

import { buildToc, type TocItem } from '@naumu/ui/article/helpers/build-toc';

// Re-exported so existing `import { TocItem } from '../helpers/docs-loader'`
// call sites keep working after the TOC builder moved to the shared module.
export type { TocItem } from '@naumu/ui/article/helpers/build-toc';

/**
 * Docs loader for the public `/docs` site.
 *
 * The corpus lives in the `@naumu/docs` package (codegen'd from `content/*.md`
 * into a committed JSON module, shared with the backend `search_docs` tool), so
 * there is no runtime `fs` read here anymore. This module adapts those articles
 * for the web UI: computing read time, the sidebar nav grouping (`GROUP_ORDER`),
 * and per-article `## ` TOCs. Safe to import from Server Components.
 */

export interface DocMeta {
	slug: string;
	title: string;
	description: string;
	group: string;
	order: number;
	/** Slug of the article this one nests under; `''` when it is top-level. */
	parent: string;
	/** Date the article was announced as new; drives the week-long "New" pill. */
	new: string;
	/** Maturity marker (`beta` / `experimental` / `''`); shown as a flask icon. */
	status: string;
	updated: string;
	readTime: string;
}

/** How long the "New" pill stays up after a doc's `new:` date. */
const NEW_BADGE_DAYS = 7;

/**
 * Whether `meta` should wear its "New" pill at `nowMs`. Callers pass a
 * client-side timestamp so the pill expires at view time, not build time.
 */
export function isDocNew(meta: DocMeta, nowMs: number): boolean {
	if (!meta.new) return false;
	const since = Date.parse(meta.new);
	if (Number.isNaN(since)) return false;
	return nowMs - since < NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
}

export interface DocEntry extends DocMeta {
	body: string;
}

/** A top-level nav row plus the subpages nested beneath it. */
export interface DocsNavItem extends DocMeta {
	children: DocMeta[];
}

export interface DocsNavGroup {
	group: string;
	items: DocsNavItem[];
}

/** Canonical group ordering for the sidebar nav. */
export const GROUP_ORDER = [
	'Getting Started',
	'Chat & Teamwork',
	'Meetings',
	'Agents & Routines',
	'Notes & Views',
	'Connectors',
	'Integrations',
	'Spaces & Account',
	'How Memory Works',
] as const;

/** URL-safe slug for a group name (e.g. 'Chat & Teamwork' → 'chat-and-teamwork'). */
export function slugifyGroup(group: string): string {
	return group
		.toLowerCase()
		.replace(/&/g, 'and')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

/** Short blurb per docs group, shown on category landing pages. */
export const GROUP_DESCRIPTIONS: Record<string, string> = {
	'Getting Started': 'Create your first space, start chatting, and learn your way around.',
	'Chat & Teamwork':
		'Talk to Naumu and your team in one place. Topics decide who sees what, Focus shows what needs you, and search finds the rest.',
	Meetings: 'Call your team in a huddle and let Naumu record, transcribe, and file the notes.',
	'Agents & Routines':
		'Add agents of your own, run Claude Code or Codex from your space, and put recurring work on a schedule.',
	'Notes & Views': 'Write notes and save views - the things you create and file into topics.',
	Connectors:
		'Plug your AI clients - Claude, ChatGPT, coding agents - into your memory. They act as you.',
	Integrations:
		'Bind team tools like Slack, Microsoft Teams, Jira, Linear, and ClickUp to a space, and trigger agents with webhooks.',
	'Spaces & Account':
		'Set up spaces, one per team or project, and manage your plan, billing, profile, and notifications.',
	'How Memory Works':
		'Look under the hood: how Naumu stores what you tell it, and how to shape that structure when you want to.',
};

const DEFAULT_GROUP = 'Getting Started';

/**
 * Synthetic nav entry for the changelog. The changelog renders the shared
 * release-notes JSON at `/docs/changelog` (a dedicated static route, not a
 * markdown article), so it lives outside the corpus: `getDocsNav` injects it so
 * it surfaces in the sidebar, the docs home, and article prev/next, while
 * `getAllDocs` (which feeds `generateStaticParams`) deliberately omits it to
 * avoid colliding with the static route.
 */
export const CHANGELOG_NAV: DocMeta = {
	slug: 'changelog',
	title: 'Changelog',
	description: 'Every Naumu release, newest first.',
	group: 'Getting Started',
	order: 9999,
	parent: '',
	new: '',
	status: '',
	updated: '',
	readTime: '',
};

function computeReadTime(body: string): string {
	const words = body.trim().split(/\s+/).filter(Boolean).length;
	const minutes = Math.max(1, Math.round(words / 200));
	return `${minutes} min read`;
}

/** Adapt a package `DocArticle` into the web `DocEntry` (adds readTime, default group). */
function toEntry(article: DocArticle): DocEntry {
	return {
		slug: article.slug,
		title: article.title,
		description: article.description,
		group: article.group || DEFAULT_GROUP,
		order: article.order,
		parent: article.parent || '',
		new: article.new || '',
		status: article.status || '',
		updated: article.updated,
		readTime: computeReadTime(article.body),
		body: article.body,
	};
}

/** Order entries by GROUP_ORDER, then `order`, then title. */
function compareDocs(a: DocMeta, b: DocMeta): number {
	const ga = GROUP_ORDER.indexOf(a.group as (typeof GROUP_ORDER)[number]);
	const gb = GROUP_ORDER.indexOf(b.group as (typeof GROUP_ORDER)[number]);
	const gaRank = ga === -1 ? GROUP_ORDER.length : ga;
	const gbRank = gb === -1 ? GROUP_ORDER.length : gb;
	if (gaRank !== gbRank) return gaRank - gbRank;

	if (a.group !== b.group) return a.group.localeCompare(b.group);
	if (a.order !== b.order) return a.order - b.order;
	return a.title.localeCompare(b.title);
}

/** Order siblings within one group: `order` first, then title. */
function compareSiblings(a: DocMeta, b: DocMeta): number {
	return a.order !== b.order ? a.order - b.order : a.title.localeCompare(b.title);
}

/** The nav-facing view of an entry - everything except the body. */
function toMeta(entry: DocEntry): DocMeta {
	return {
		slug: entry.slug,
		title: entry.title,
		description: entry.description,
		group: entry.group,
		order: entry.order,
		parent: entry.parent,
		new: entry.new,
		status: entry.status,
		updated: entry.updated,
		readTime: entry.readTime,
	};
}

/**
 * The single nesting rule, applied against an already-indexed corpus. A doc
 * nests only under a doc in the same group that is itself top-level; anything
 * that fails to resolve (unknown slug, another group, itself, or a two-deep
 * chain) is top-level, so no article can silently vanish from the nav.
 *
 * Everything that needs a doc's parent goes through here - the nav
 * (`nestDocs`) and the article breadcrumb (`getDoc` via `resolveDocParent`) -
 * so the two can never disagree.
 */
function resolveParentIn<T extends DocMeta>(bySlug: Map<string, T>, doc: T): T | null {
	if (!doc.parent || doc.parent === doc.slug) return null;
	const parent = bySlug.get(doc.parent);
	if (!parent || parent.group !== doc.group) return null;
	// Only one level deep: a doc whose parent is itself nested stays top-level.
	const grandparent = parent.parent ? bySlug.get(parent.parent) : undefined;
	if (grandparent && grandparent.group === parent.group && grandparent.slug !== parent.slug) {
		return null;
	}
	return parent;
}

/**
 * The doc `doc` nests under within `docs`, or `null` when it is top-level by
 * the same rule the nav uses.
 */
export function resolveDocParent<T extends DocMeta>(docs: T[], doc: T): T | null {
	return resolveParentIn(new Map(docs.map((d) => [d.slug, d])), doc);
}

/**
 * Resolve `parent` frontmatter into ONE level of nesting, preserving the input
 * order for top-level rows. Nesting eligibility is `resolveParentIn` above.
 */
function nestDocs<T extends DocMeta>(docs: T[]): { item: T; children: T[] }[] {
	const bySlug = new Map(docs.map((doc) => [doc.slug, doc]));

	const childrenBySlug = new Map<string, T[]>();
	const topLevel: T[] = [];
	for (const doc of docs) {
		const parent = resolveParentIn(bySlug, doc);
		if (!parent) {
			topLevel.push(doc);
			continue;
		}
		const siblings = childrenBySlug.get(parent.slug);
		if (siblings) siblings.push(doc);
		else childrenBySlug.set(parent.slug, [doc]);
	}

	return topLevel.map((item) => ({
		item,
		children: (childrenBySlug.get(item.slug) ?? []).slice().sort(compareSiblings),
	}));
}

/**
 * Every article, flat, in canonical reading order: groups by GROUP_ORDER, then
 * `order`/title, with each subpage spliced in immediately after its parent.
 * This is the sequence prev/next and `generateStaticParams` walk.
 */
export function getAllDocs(): DocEntry[] {
	const entries = getDocArticles().map(toEntry).sort(compareDocs);
	return nestDocs(entries).flatMap(({ item, children }) => [item, ...children]);
}

/** Nav items as a flat list - every parent immediately followed by its children. */
export function flattenNavItems(items: DocsNavItem[]): DocMeta[] {
	return items.flatMap((item) => [item, ...item.children]);
}

/**
 * Articles grouped by GROUP_ORDER; items sorted by order then title, with
 * `parent:` subpages nested under their parent row.
 */
export function getDocsNav(): DocsNavGroup[] {
	const docs = getAllDocs();

	const byGroup = new Map<string, DocMeta[]>();
	for (const doc of docs) {
		const meta = toMeta(doc);
		const list = byGroup.get(doc.group);
		if (list) list.push(meta);
		else byGroup.set(doc.group, [meta]);
	}

	// Inject the JSON-driven changelog (not part of the markdown corpus). order
	// 9999 sorts it to the end of its group below.
	const changelogGroup = byGroup.get(CHANGELOG_NAV.group);
	if (changelogGroup) changelogGroup.push(CHANGELOG_NAV);
	else byGroup.set(CHANGELOG_NAV.group, [CHANGELOG_NAV]);

	// Known groups first, in GROUP_ORDER; then any extra groups alphabetically.
	const knownGroups = GROUP_ORDER.filter((g) => byGroup.has(g));
	const extraGroups = Array.from(byGroup.keys())
		.filter((g) => !GROUP_ORDER.includes(g as (typeof GROUP_ORDER)[number]))
		.sort((a, b) => a.localeCompare(b));

	const ordered: DocsNavGroup[] = [];
	for (const group of [...knownGroups, ...extraGroups]) {
		const sorted = (byGroup.get(group) ?? []).slice().sort(compareSiblings);
		const items = nestDocs(sorted).map(({ item, children }) => ({ ...item, children }));
		ordered.push({ group, items });
	}

	return ordered;
}

/** One `{ category }` param per nav group, for `generateStaticParams`. */
export function getDocsGroupSlugs(): { category: string }[] {
	return getDocsNav().map((g) => ({ category: slugifyGroup(g.group) }));
}

/**
 * Resolve a category slug back to its group, description, and items. Items come
 * back flat (each parent followed by its subpages) so the category page keeps
 * its single card grid. `null` if unknown.
 */
export function getDocsGroupBySlug(
	categorySlug: string
): { group: string; description: string; items: DocMeta[] } | null {
	const match = getDocsNav().find((g) => slugifyGroup(g.group) === categorySlug);
	if (!match) return null;

	return {
		group: match.group,
		description:
			GROUP_DESCRIPTIONS[match.group] ?? `Guides and reference for ${match.group} in Naumu.`,
		items: flattenNavItems(match.items),
	};
}

/**
 * Single article by slug, with parsed body + `## ` TOC. `parent` is the article
 * this one nests under in the nav (for the breadcrumb), resolved by
 * `resolveDocParent` so an unresolvable `parent:` - unknown slug, another
 * group, itself, or a two-deep chain - yields `null` here exactly as it stays
 * top-level in the sidebar. `null` if the article is not found.
 */
export function getDoc(
	slug: string
): { meta: DocMeta; body: string; toc: TocItem[]; parent: DocMeta | null } | null {
	const docs = getAllDocs();
	const entry = docs.find((d) => d.slug === slug);
	if (!entry) return null;

	const { body, ...meta } = entry;
	const parentEntry = resolveDocParent(docs, entry);
	return {
		meta,
		body,
		toc: buildToc(body),
		parent: parentEntry ? toMeta(parentEntry) : null,
	};
}
